import { RATING_STARS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import type { ConversationItem } from "./inbox-model";
import { ratingOf } from "./rating";

/** Thumbs ratings are stored as 5 (up) or 1 (down); anything from 4 stars counts as positive. */
const POSITIVE_FROM = 4;

/** The visitor's rating of the conversation: stars or a thumb (shape, not colour), their comment and when. Nothing until rated. */
export function RatingBlock({ conversation }: { conversation: Pick<ConversationItem, "rating" | "rating_scale" | "rating_comment" | "rated_at"> }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const rated = ratingOf(conversation);
  if (!rated) return null;
  const rating = rated.value;
  const thumbs = rated.scale === "thumbs";
  const positive = rating >= POSITIVE_FROM;
  return (
    <div className="rounded-lg border border-line bg-surface-2 p-3" data-testid="rating-block">
      <div className="label">{t("engage.inbox.rating.title")}</div>
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-0.5 text-primary-ink" aria-hidden="true">
          {thumbs ? (
            <Icon name={positive ? "thumbs-up" : "thumbs-down"} className="size-5" />
          ) : (
            Array.from({ length: RATING_STARS }, (_, index) => <Icon key={index} name="star" className={index < rating ? "size-4 fill-current" : "size-4 text-fg-subtle"} />)
          )}
        </span>
        <span className="text-sm font-bold">{thumbs ? t(positive ? "engage.inbox.rating.up" : "engage.inbox.rating.down") : t("engage.inbox.rating.outOf", { rating, max: RATING_STARS })}</span>
      </div>
      {rated.comment ? <blockquote className="mt-2 border-l-2 border-line pl-3 text-xs text-fg-muted">{rated.comment}</blockquote> : null}
      {conversation.rated_at ? <p className="mt-1.5 text-[11px] text-fg-subtle">{t("engage.inbox.rating.ratedAt", { time: fmt.dateTime(conversation.rated_at) })}</p> : null}
    </div>
  );
}
